import { ref, set, update, remove, push, onValue, onChildAdded, onDisconnect, serverTimestamp } from 'firebase/database';

// How Battle Arena talks to Firebase. Two versions with the same shape:
//  - firebaseNet: Firebase's live connection (the website)
//  - restNet: Firebase's web API (inside Discord, whose proxy blocks the live connection)
// Paths are relative to the arena's own node (arena, or arenaActivity/<instance> in Discord).

export function firebaseNet(database, root = 'arena') {
  const at = (p) => ref(database, p ? `${root}/${p}` : root);
  return {
    onPlayers: (cb) => onValue(at('players'), (s) => cb(s.val() || {})),
    onRound: (cb) => onValue(at('round'), (s) => cb(s.val())),
    onClockOffset: (cb) => onValue(ref(database, '.info/serverTimeOffset'), (s) => cb(s.val() || 0)),
    onHits: (uid, cb) => onChildAdded(at(`players/${uid}/hits`), (s) => { remove(s.ref).catch(() => {}); cb(s.val()); }),
    set: (p, v) => set(at(p), v),
    update: (p, v) => update(at(p), v),
    remove: (p) => remove(at(p)),
    push: (p, v) => push(at(p), v),
    join: async (uid, v) => {
      await set(at(`players/${uid}`), { ...v, joinedAt: serverTimestamp() });
      onDisconnect(at(`players/${uid}`)).remove();
    },
    leave: (uid) => remove(at(`players/${uid}`)),
  };
}

const FAST_MS = 50;       // a new read this often during a battle (when reading, not streaming)
const SLOW_MS = 800;      // between reads in the lobby
const MAX_READS = 3;      // reads on their way at once, so a slow trip through Discord doesn't hold up the next one
const MAX_WRITES = 3;     // the same for updates to one place
const STREAM_WAIT_MS = 4000;   // the stream has this long to send something, or reading takes over
const STREAM_QUIET_MS = 45000; // Firebase sends a keep-alive every 30s, so this long without anything means it's dead
const BEAT_MS = 2000;     // how often you check in, so the host can tell you're still here
const TS = { '.sv': 'timestamp' };

// A copy of obj with the value at parts swapped (null removes it). New objects along the way, so the arena can tell
// what changed by comparing the old and new
function setAt(obj, parts, val) {
  if (!parts.length) return val;
  const [k, ...rest] = parts;
  const base = obj && typeof obj === 'object' ? obj : {};
  const child = setAt(base[k], rest, val);
  const out = { ...base };
  if (child === null || child === undefined) delete out[k]; else out[k] = child;
  return out;
}

export function restNet(base, root) {
  const url = (p) => `${base}/${p ? `${root}/${p}` : root}.json`;
  const send = (p, method, body, keepalive = false) =>
    fetch(url(p), { method, keepalive, body: body === undefined ? undefined : JSON.stringify(body) })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`Firebase ${method} ${p}: HTTP ${r.status}`))));

  const playerCbs = new Set(), roundCbs = new Set(), offsetCbs = new Set(), hitCbs = new Map();
  const seenHits = new Set();
  const newest = new Map();  // uid -> their newest state seen (by _q), in case an older update lands after a newer one
  let tree = {}, offset = 0, battle = false;
  const active = () => playerCbs.size || roundCbs.size || hitCbs.size;
  const setOffset = (serverTime, t0) => {
    if (typeof serverTime !== 'number') return;
    offset = serverTime - (t0 + Date.now()) / 2;
    offsetCbs.forEach((cb) => cb(offset));
  };

  // The arena changed: hand it out to whoever's listening
  const dispatch = () => {
    const players = { ...(tree.players || {}) };
    for (const [uid, p] of Object.entries(players)) {
      const q = p && typeof p._q === 'number' ? p._q : null;
      const last = newest.get(uid);
      if (q !== null && last && q < last._q) players[uid] = last;
      else if (q !== null) newest.set(uid, p);
    }
    battle = !!tree.round?.phase && tree.round.phase !== 'lobby';
    roundCbs.forEach((cb) => cb(tree.round ?? null));
    playerCbs.forEach((cb) => cb(players));
    hitCbs.forEach((cb, uid) => {
      for (const [key, hit] of Object.entries(players[uid]?.hits || {})) {
        if (seenHits.has(key)) continue;
        seenHits.add(key);
        send(`players/${uid}/hits/${key}`, 'DELETE').catch(() => {});
        cb(hit);
      }
    });
  };

  // Best: Firebase streams every change as it happens. If Discord's proxy won't pass the stream through, the arena is
  // read over and over instead, several reads at a time
  let mode = 'off', es = null, waitTimer = null, quietTimer = null, pollTimer = null;
  let readSeq = 0, appliedSeq = 0, reading = 0;
  const read = () => {
    const seq = ++readSeq;
    reading++;
    send('', 'GET').then((data) => {
      if (seq <= appliedSeq || mode !== 'poll') return;  // a later read already arrived
      appliedSeq = seq;
      tree = data || {};
      dispatch();
    }).catch(() => {}).finally(() => { reading--; });
  };
  const pollLoop = () => {
    if (mode !== 'poll') return;
    if (reading < (battle ? MAX_READS : 1)) read();
    pollTimer = setTimeout(pollLoop, battle ? FAST_MS : SLOW_MS);
  };
  const stopStream = () => {
    clearTimeout(waitTimer); clearTimeout(quietTimer);
    if (es) es.close();
    es = null;
  };
  const toPolling = (why) => {
    if (mode === 'poll' || mode === 'off') return;
    console.warn(`Battle Arena: no live stream (${why}), reading instead`);
    stopStream();
    mode = 'poll';
    pollLoop();
  };
  const start = () => {
    if (mode !== 'off') return;
    if (typeof EventSource === 'undefined') { mode = 'poll'; pollLoop(); return; }
    mode = 'stream';
    es = new EventSource(url(''));
    let got = false;
    const heard = () => {
      got = true;
      clearTimeout(waitTimer); clearTimeout(quietTimer);
      quietTimer = setTimeout(() => toPolling('it went quiet'), STREAM_QUIET_MS);
    };
    waitTimer = setTimeout(() => { if (!got) toPolling('nothing arrived'); }, STREAM_WAIT_MS);
    const onChange = (kind) => (e) => {
      heard();
      let msg;
      try { msg = JSON.parse(e.data); } catch { return; }
      if (!msg || typeof msg.path !== 'string') return;
      const parts = msg.path.split('/').filter(Boolean);
      if (kind === 'put') tree = setAt(tree, parts, msg.data) || {};
      else for (const [k, v] of Object.entries(msg.data || {})) tree = setAt(tree, [...parts, ...k.split('/').filter(Boolean)], v) || {};
      dispatch();
    };
    es.addEventListener('put', onChange('put'));
    es.addEventListener('patch', onChange('patch'));
    es.addEventListener('keep-alive', heard);
    es.addEventListener('cancel', () => toPolling('Firebase cancelled it'));
    es.addEventListener('auth_revoked', () => toPolling('Firebase cancelled it'));
    es.onerror = () => { if (!got) toPolling("Discord didn't allow it"); };  // once it's worked, it reconnects by itself
  };
  const stop = () => {
    stopStream();
    clearTimeout(pollTimer);
    mode = 'off';
  };
  const listen = (set_, key, cb) => {
    if (key === undefined) set_.add(cb); else set_.set(key, cb);
    if (mode === 'off') start(); else if (Object.keys(tree).length) dispatch();
    return () => {
      if (key === undefined) set_.delete(cb); else set_.delete(key);
      if (!active()) stop();
    };
  };
  // When reading, show your own changes straight away in the lobby (the stream does this by itself)
  const refreshSoon = () => { if (mode === 'poll' && !battle && reading === 0) read(); };

  // A few updates to the same place can be on their way at once. Past that, later ones are merged and sent next. Your
  // own state carries a number that only goes up (_q), so if an older one lands after a newer one it's ignored
  let lastQ = 0;
  const nextQ = () => { lastQ = Math.max(lastQ + 1, Date.now()); return lastQ; };
  const isPlayer = (p) => /^players\/[^/]+$/.test(p);
  const inflight = new Map(), pending = new Map();
  const patch = (p, v) => {
    if ((inflight.get(p) || 0) >= MAX_WRITES) { pending.set(p, { ...(pending.get(p) || {}), ...v }); return Promise.resolve(); }
    inflight.set(p, (inflight.get(p) || 0) + 1);
    return send(p, 'PATCH', isPlayer(p) ? { ...v, _q: nextQ() } : v).finally(() => {
      inflight.set(p, inflight.get(p) - 1);
      const next = pending.get(p);
      pending.delete(p);
      if (next) patch(p, next).catch(() => {}); else refreshSoon();
    });
  };
  const forget = (p) => { for (const k of [...pending.keys()]) if (k === p || k.startsWith(`${p}/`)) pending.delete(k); };

  // You're in the arena: check in every few seconds (which also keeps the clocks lined up), and leave when the page closes
  let beat = null, onHide = null;
  const stopBeat = () => {
    clearInterval(beat); beat = null;
    if (onHide) window.removeEventListener('pagehide', onHide);
    onHide = null;
  };

  return {
    onPlayers: (cb) => listen(playerCbs, undefined, cb),
    onRound: (cb) => listen(roundCbs, undefined, cb),
    onClockOffset: (cb) => { offsetCbs.add(cb); cb(offset); return () => offsetCbs.delete(cb); },
    onHits: (uid, cb) => listen(hitCbs, uid, cb),
    set: (p, v) => { forget(p); return send(p, 'PUT', v).then(refreshSoon); },
    update: (p, v) => patch(p, v),
    remove: (p) => { forget(p); return send(p, 'DELETE').then(refreshSoon); },
    push: (p, v) => send(p, 'POST', v),
    join: async (uid, v) => {
      stopBeat();
      const t0 = Date.now();
      const saved = await send(`players/${uid}`, 'PUT', { ...v, joinedAt: TS, seen: TS, _q: nextQ() });
      setOffset(saved?.seen, t0);
      beat = setInterval(() => {
        const t = Date.now();
        send(`players/${uid}`, 'PATCH', { seen: TS }).then((r) => setOffset(r?.seen, t)).catch(() => {});
      }, BEAT_MS);
      onHide = () => send(`players/${uid}`, 'DELETE', undefined, true).catch(() => {});
      window.addEventListener('pagehide', onHide);
      refreshSoon();
    },
    leave: (uid) => {
      stopBeat();
      forget(`players/${uid}`);
      return send(`players/${uid}`, 'DELETE').then(refreshSoon);
    },
  };
}
